import type { CoreSkill } from "../../types";

export const coreSkills: CoreSkill[] = [
  {
    id: "arcane-prodigy",
    name: "Arcane Prodigy",
    description:
      "You gain +2 MP and can spend 2 points on a mix of spells from any spell list. This skill can be taken multiple times.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +2 MP and can spend 2 points on a mix of spells from any spell list. This skill can be taken multiple times.",
      },
    ],
    repeatable: true,
    effects: [{ type: "mp", value: 2 }],
    spellPoints: { listId: "any", points: 2 },
  },
  {
    id: "armour-training",
    name: "Armour Training",
    description:
      "You are trained in the maintenance and use of armour of all kinds. Buying this skill allows you to use armour (the type is tied to your Wealth Level).",
    full_description: [
      {
        type: "paragraph",
        text: "You are trained in the maintenance and use of armour of all kinds. Buying this skill allows you to use armour — but the type of armour you have access to is tied to your Wealth Level. If you are of a certain social class, you automatically gain access to the associated armour.",
      },
      {
        type: "table",
        headers: [
          "Armour",
          "Wealth Level",
          "AP",
          "Damage Reduction",
          "Examples",
        ],
        rows: [
          [
            "Scrap Armour",
            "1 (Peasant)",
            "+1",
            "—",
            "Layered rags, random junk",
          ],
          [
            "Light Armour",
            "2 (Merchant/Artisan)",
            "+1",
            "-1",
            "Leather, furs and animal hides",
          ],
          [
            "Medium Armour",
            "3 (Wealthy)",
            "+3",
            "-1",
            "Metal breastplate, chainmail",
          ],
          [
            "Heavy Armour",
            "4 (Noble)",
            "+3",
            "-2",
            "As Medium Armour plus a helmet",
          ],
        ],
      },
      {
        type: "paragraph",
        text: "Armour Points (AP) act as a buffer that absorbs damage before your HP is affected. Damage is taken from your AP before you lose any HP. AP can be restored after a battle by taking at least 1 minute of roleplay to readjust, fix, and polish up your armour — if you know how to wear your armour, you know how to maintain it, too.",
      },
      {
        type: "paragraph",
        text: "The damage reduction of armour can reduce damage to 0, making you immune to small blows from weak enemies, so long as they don't hit you on any exposed areas. However, this only applies to physical blows — armour does not protect against magical attacks such as the Blast spell!",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description: "Can use armour (type based on Wealth Level)",
      },
    ],
  },
  {
    id: "endurance-training",
    name: "Endurance Training",
    description:
      "You gain +2 HP (cannot exceed maximum HP of 8). Additionally, when you take a Short Rest, you may recover all your HP without spending a silver coin. This skill can be taken multiple times.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +2 HP. This skill can be taken multiple times, but you cannot exceed the maximum HP score of 8.",
      },
      {
        type: "paragraph",
        text: "Additionally, when you take a Short Rest, you may recover all of your HP without needing to spend a silver coin.",
      },
    ],
    repeatable: true,
    maxTimes: 3,
    effects: [{ type: "hp", value: 2 }],
  },
  {
    id: "firearms-training",
    name: "Firearms Training",
    description:
      "You are trained in the use of firearms (pistols deal 2 damage, rifles/shotguns deal 3 damage). No firearm can hold more than 6 shots before reloading.",
    full_description: [
      {
        type: "paragraph",
        text: "Exotic projectile weapons have been independently developed several times across the multiverse: whether powered by magic, clockwork, or gunpowder, the essential effect is always the same.",
      },
      {
        type: "table",
        headers: ["Weapon", "Damage", "Examples"],
        rows: [
          ["One-handed", "2", "Pistols"],
          ["Two-handed", "3", "Rifles, Shotguns"],
        ],
      },
      {
        type: "paragraph",
        text: "Firearms in Ultimate Warlock are not advanced enough (yet) to fire automatically — and no firearm can hold a magazine of more than 6 shots before needing to be reloaded.",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description:
          "Can use firearms (pistols 2 damage, rifles 3 damage, max 6 shots)",
      },
    ],
  },
  {
    id: "inherited-wealth",
    name: "Inherited Wealth",
    description:
      "Increase your Wealth Level by 1. Every player begins at Wealth Level 1 (Peasant). This skill can be taken multiple times, but cannot be used to reach Wealth Level 5 (Royalty).",
    full_description: [
      {
        type: "paragraph",
        text: "Congratulations! You've been born into generational wealth. Increase your Wealth Level by 1.",
      },
      {
        type: "paragraph",
        text: "Every player begins at Wealth Level 1 (Peasant). This skill can be taken multiple times, but cannot be used to reach Wealth Level 5 (Royalty).",
      },
    ],
    repeatable: true,
    maxTimes: 3,
    effects: [{ type: "wealthLevel", value: 1 }],
  },
  {
    id: "ill-gotten-gains",
    name: "Ill-Gotten Gains",
    description:
      "Whenever you drop an enemy combatant to 0 HP, you may take 1 silver coin from the public bank. You may also pickpocket NPCs by attaching a clothes peg to their costume without being detected.",
    full_description: [
      {
        type: "paragraph",
        text: "People watch their wallets around you, check their rings are still on their fingers after every handshake, and lock their doors and windows if they see you come into town.",
      },
      {
        type: "paragraph",
        text: "You have no qualms about relieving the dead of their valuables. Whenever you drop an enemy combatant to 0 HP, you may then take 1 silver coin from the public bank at your convenience, to represent your unscrupulous frisking of the body for treasures.",
      },
      {
        type: "paragraph",
        text: "In addition, you may surreptitiously lighten the load of any non-player characters (NPCs) you encounter. If you are able to attach a clothes peg to the costume of an NPC without being detected, you have successfully 'pickpocketed' them, and may help yourself to a silver piece as above.",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description:
          "Gain 1 silver when you drop enemies to 0 HP; can pickpocket NPCs",
      },
    ],
  },
  {
    id: "practical-medicine",
    name: "Practical Medicine",
    description:
      "You can stabilise Bleeding Out characters with 1 minute of roleplay and medical gear, restoring them to 1 HP. You can also cure Poisoned characters by expending 1 silver coin and 1 minute of roleplay.",
    full_description: [
      {
        type: "paragraph",
        text: "You can stabilise characters who are Bleeding Out with 1 minute of uninterrupted roleplay and the use of appropriate medical gear such as bandages, medicines, leeches, healing crystals, or homeopathic remedies.",
      },
      {
        type: "paragraph",
        text: "While being stabilised, the timer is paused for Bleeding Out. Successfully stabilising a character ends Bleeding Out and restores them to 1 HP.",
      },
      {
        type: "paragraph",
        text: "In addition, if a character is Poisoned, you can cure them of the condition by expending 1 silver coin on antivenom, and taking at least 1 minute of roleplay to cure them.",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description:
          "Can stabilise Bleeding Out characters; can cure Poison with 1 silver",
      },
    ],
  },
  {
    id: "silver-tongued-rascal",
    name: "Silver-Tongued Rascal",
    description:
      "After roleplaying a conversation with any character for at least 1 minute, you may Charm them (no MP cost, once per character per day). Cannot use Charm's memory wiping ability.",
    full_description: [
      {
        type: "paragraph",
        text: "After roleplaying a conversation with any character for at least 1 minute, you may Charm them, exactly as though you had used the Charm spell — except it costs no magic points, does not count as a spell, and you cannot use Charm's memory wiping ability.",
      },
      {
        type: "paragraph",
        text: "Inform your target out of character that they have been Charmed and regard you as a trustworthy friend for the next 5 minutes. This only works on each character once per day.",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description:
          "Can Charm characters after 1 min conversation; once per character per day",
      },
    ],
  },
  {
    id: "weapon-shield-training",
    name: "Weapon & Shield Training",
    description:
      "You can wield shields, medium weapons (60cm+, 2 damage), and large weapons (requires 2 hands, 3 damage). Anyone can use small weapons (<60cm, 1 damage) without this skill.",
    full_description: [
      {
        type: "paragraph",
        text: "Anyone can use small weapons, but you have led the life of a warrior, and are skilled in the use of advanced armaments. You can wield shields, medium weapons, and large weapons.",
      },
      {
        type: "table",
        headers: ["Weapon Size", "Damage", "Examples"],
        rows: [
          ["Small (< 60cm)", "1", "Daggers, Handaxes, Shortswords"],
          ["Medium (60cm+)", "2", "Swords, Axes, Maces"],
          ["Large (60cm+, requires 2 hands)", "3", "Polearms"],
        ],
      },
      {
        type: "paragraph",
        text: "Any damaging hit taken by a shield is completely negated. Shields do not protect you against spells that do not deal damage, such as Fear or Restrict. Shields cannot be larger than 110cm.",
      },
    ],
    repeatable: false,
    effects: [
      {
        type: "special",
        description:
          "Can use shields, medium weapons (2 damage), and large weapons (3 damage, 2-handed)",
      },
    ],
  },
];
